
import React, { useState } from 'react';

const StartNowPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    employees: '',
    urgency: '',
    concerns: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d447iumpguuav66rgbe0', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          employees: '',
          urgency: '',
          concerns: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 via-green-500 to-green-400 text-white overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/0dc88f49443fe03c6aeaa66fa527c406.jpg')`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center space-y-8">
            <h1 className="text-5xl lg:text-6xl font-black leading-tight tracking-tight">
              まずは試してみませんか？<br />
              <span className="text-green-200 font-extrabold">「社長の右腕」を、今すぐ持つチャンス！</span>
            </h1>
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="flex items-center justify-center space-x-4 text-2xl font-bold">
                <i className="ri-check-line text-green-200 text-3xl w-8 h-8 flex items-center justify-center"></i>
                <span>2カ月のお試し顧問（正規料金の60%）で導入リスクなし</span>
              </div>
              <div className="flex items-center justify-center space-x-4 text-2xl font-bold">
                <i className="ri-check-line text-green-200 text-3xl w-8 h-8 flex items-center justify-center"></i>
                <span>6カ月の更新制で、継続の柔軟性あり</span>
              </div>
              <div className="flex items-center justify-center space-x-4 text-2xl font-bold">
                <i className="ri-check-line text-green-200 text-3xl w-8 h-8 flex items-center justify-center"></i>
                <span>「社長の右腕」を、今すぐ持つチャンス！</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#contact-form" 
                className="bg-white text-green-600 px-8 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                📩 今すぐお問い合わせ
              </a>
              <a 
                href="/trial" 
                className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                試し顧問の詳細を見る
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* New Trial Benefits Section */}
      <section className="py-16 bg-green-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-lg shadow-xl border-l-8 border-green-500">
            <h2 className="text-4xl font-black text-center text-gray-900 mb-8 tracking-tight">
              まずは試してみませんか？
            </h2>
            <div className="space-y-6">
              <div className="flex items-center space-x-4 text-xl font-bold text-gray-700">
                <i className="ri-check-line text-green-500 text-3xl w-8 h-8 flex items-center justify-center bg-green-100 rounded-full"></i>
                <span>2カ月のお試し顧問（正規料金の60%）で導入リスクなし</span>
              </div>
              <div className="flex items-center space-x-4 text-xl font-bold text-gray-700">
                <i className="ri-check-line text-green-500 text-3xl w-8 h-8 flex items-center justify-center bg-green-100 rounded-full"></i>
                <span>6カ月の更新制で、継続の柔軟性あり</span>
              </div>
              <div className="flex items-center space-x-4 text-xl font-bold text-gray-700">
                <i className="ri-check-line text-green-500 text-3xl w-8 h-8 flex items-center justify-center bg-green-100 rounded-full"></i>
                <span>「社長の右腕」を、今すぐ持つチャンス！</span>
              </div>
            </div>
            <div className="text-center mt-8">
              <a 
                href="#contact-form" 
                className="bg-green-500 hover:bg-green-600 text-white px-12 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 inline-block"
              >
                📩 今すぐお問い合わせ
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Urgent Message Section */}
      <section className="py-16 bg-orange-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-lg shadow-xl border-l-8 border-orange-500">
            <h2 className="text-4xl font-black text-center text-gray-900 mb-8 tracking-tight">
              📩 今すぐお問い合わせください！
            </h2>
            <div className="text-center space-y-6">
              <p className="text-2xl text-gray-700 leading-relaxed font-medium">
                経営の悩みを、一人で抱え込む必要はありません。
              </p>
              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-4 text-xl font-medium text-gray-700">
                  <i className="ri-focus-line text-blue-500 text-2xl w-8 h-8 flex items-center justify-center"></i>
                  <span>社長が本業に集中できる環境を、今こそ整えましょう。</span>
                </div>
                <div className="bg-yellow-100 p-6 rounded-lg border-l-4 border-yellow-500">
                  <div className="flex items-start space-x-4">
                    <i className="ri-information-line text-yellow-600 text-2xl w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1"></i>
                    <p className="text-gray-700 leading-relaxed text-lg font-medium">
                      現在お問い合わせが多いため、担当コンサルタントの状況により、お受けできるタイミングがございますので、ご了承ください。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-center text-gray-900 mb-12 tracking-tight">
            今すぐ始める<span className="text-green-600">3つの理由</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-green-50 p-8 rounded-lg shadow-xl border-t-4 border-green-500 hover:shadow-2xl transition-shadow">
              <div className="text-center mb-6">
                <i className="ri-shield-check-line text-green-500 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">低リスクでスタート</h3>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                <span className="font-bold text-green-600">2カ月のお試し期間</span>で正規料金の<span className="font-bold text-orange-600">60%</span>という特別価格。大きな投資をする前に効果を実感していただけます。
              </p>
            </div>
            
            <div className="bg-blue-50 p-8 rounded-lg shadow-xl border-t-4 border-blue-500 hover:shadow-2xl transition-shadow">
              <div className="text-center mb-6">
                <i className="ri-refresh-line text-blue-500 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">柔軟な更新制</h3>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                <span className="font-bold text-blue-600">6カ月の更新制</span>なので、効果を実感してから継続をご判断いただけます。長期契約の縛りはありません。
              </p>
            </div>
            
            <div className="bg-purple-50 p-8 rounded-lg shadow-xl border-t-4 border-purple-500 hover:shadow-2xl transition-shadow">
              <div className="text-center mb-6">
                <i className="ri-user-star-line text-purple-500 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">即戦力パートナー</h3>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                <span className="font-bold text-purple-600">「社長の右腕」</span>として、すぐに経営をサポート。育成期間なしで即座に効果を実感していただけます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Immediate Action Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-center text-gray-900 mb-12 tracking-tight">
            今すぐ行動すべき<span className="text-red-600">理由</span>
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-red-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">⏰ 時間は待ってくれません</h3>
                  <p className="text-gray-700 leading-relaxed font-medium">
                    経営の悩みを先延ばしにするほど、問題は複雑化し、解決が困難になります。
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">📈 競合他社との差が広がる</h3>
                  <p className="text-gray-700 leading-relaxed font-medium">
                    効率的な経営体制を整えた企業との差は、日々広がっています。
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-blue-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">💡 機会損失を防ぐ</h3>
                  <p className="text-gray-700 leading-relaxed font-medium">
                    社長が本業に集中できれば、新たなビジネスチャンスを掴むことができます。
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Business%20urgency%20and%20opportunity%20concept%20showing%20clock%20with%20business%20growth%20charts%2C%20time-sensitive%20decision%20making%20illustration%2C%20professional%20corporate%20success%20visualization%20with%20upward%20trending%20arrows%20and%20golden%20opportunity%20light%2C%20clean%20modern%20design%20emphasizing%20immediate%20action&width=600&height=500&seq=urgent-action&orientation=landscape"
                alt="今すぐ行動すべき理由"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-6 tracking-tight">
              📩 今すぐ<span className="text-green-600">お問い合わせ</span>
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed font-medium">
              経営の悩みを一人で抱え込まず、今すぐご相談ください。<br />
              24時間以内にご連絡いたします。
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg shadow-xl p-8">
            {submitStatus === 'success' && (
              <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-500 text-2xl mr-3"></i>
                  <p className="text-green-700 font-semibold text-lg">
                    お問い合わせありがとうございます。24時間以内にご連絡いたします。
                  </p>
                </div>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-500 text-2xl mr-3"></i>
                  <p className="text-red-700 font-semibold text-lg">
                    送信に失敗しました。もう一度お試しください。
                  </p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="start-consultation">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-lg font-semibold text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                    placeholder="山田太郎"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-lg font-semibold text-gray-700 mb-2">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                    placeholder="株式会社○○"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-lg font-semibold text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                    placeholder="example@company.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-lg font-semibold text-gray-700 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                    placeholder="03-1234-5678"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="employees" className="block text-lg font-semibold text-gray-700 mb-2">
                    従業員数 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="employees"
                    name="employees"
                    value={formData.employees}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="1-5人">1-5人</option>
                    <option value="6-10人">6-10人</option>
                    <option value="11-20人">11-20人</option>
                    <option value="21-30人">21-30人</option>
                    <option value="31人以上">31人以上</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="urgency" className="block text-lg font-semibold text-gray-700 mb-2">
                    相談の緊急度 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="urgency"
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="至急（1週間以内）">至急（1週間以内）</option>
                    <option value="急ぎ（2週間以内）">急ぎ（2週間以内）</option>
                    <option value="通常（1ヶ月以内）">通常（1ヶ月以内）</option>
                    <option value="検討中">検討中</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="concerns" className="block text-lg font-semibold text-gray-700 mb-2">
                  現在の経営課題 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="concerns"
                  name="concerns"
                  value={formData.concerns}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg resize-none"
                  placeholder="お金の流れが見えない、社員との温度差、経営に集中できないなど、現在お困りのことをお聞かせください（500文字以内）"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.concerns.length}/500文字</p>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-lg font-semibold text-gray-700 mb-2">
                  その他ご質問・ご要望
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg resize-none"
                  placeholder="ご質問やご要望がございましたらお聞かせください（500文字以内）"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>
              
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-12 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
                >
                  {isSubmitting ? '送信中...' : '📩 今すぐお問い合わせ'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-black mb-6 tracking-tight">
            経営の悩みを、一人で抱え込む時代は終わりました
          </h2>
          <p className="text-2xl leading-

relaxed font-medium mb-8">
            「社長の右腕」として、あなたの経営を全力でサポートします。<br />
            今すぐ行動して、経営の未来を変えましょう。
          </p>
          <a 
            href="#contact-form" 
            className="bg-white text-green-600 px-12 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 inline-block"
          >
            📩 今すぐお問い合わせ
          </a>
        </div>
      </section>
    </div>
  );
};

export default StartNowPage;
